/* Give likely next clicks a head start, using the real link URL and normal navigation. */
(() => {
  const loader = document.currentScript;
  const root = new URL(loader.dataset.storeRoot || '/', location.href).pathname.replace(/\/$/, '') + '/';
  const native = !!HTMLScriptElement.supports?.('speculationrules');
  const prefetched = new Map();
  let rendered;
  let hoverLink;
  let hoverTimer;
  let engaged = false;

  function allowed() {
    const connection = navigator.connection;
    return !document.prerendering && document.visibilityState === 'visible' && navigator.onLine !== false &&
      !connection?.saveData && !['slow-2g', '2g', '3g'].includes(connection?.effectiveType);
  }

  function destination(link) {
    if (!link?.matches('a[href]') || !link.isConnected || link.hasAttribute('download') ||
        (link.target && link.target !== '_self') || link.closest('[hidden], [inert], [aria-hidden="true"], [data-no-prefetch]')) return;
    let url;
    try { url = new URL(link.href, location.href); } catch (_) { return; }
    if (url.origin !== location.origin || !url.pathname.startsWith(root) || url.username || url.password) return;
    url.hash = '';
    if (url.pathname === location.pathname && url.search === location.search) return;
    const path = url.pathname.slice(root.length).replace(/\/$/, '');
    const keys = [...url.searchParams.keys()];
    if (/^(?:collections\/[\w%-]+\/)?products\/[\w%-]+$/.test(path)) {
      // Shopify search and recommendation results can carry attribution parameters.
      if (keys.every(key => ['variant','_pos','_sid','_ss','_psq','_v','pr_prod_strat','pr_rec_id','pr_rec_pid','pr_ref_pid','pr_seq'].includes(key))) return url.href;
    } else if (/^collections(?:\/[\w%-]+)?$/.test(path)) {
      if (keys.every(key => key === 'page' || key === 'sort_by' || key.startsWith('filter.'))) return url.href;
      if (path === 'collections' && /^\?view=ts-(?:underground|aerial|fiber-optics|copper|racks-cabinets|tools-equipment)$/.test(url.search)) return url.href;
    } else if (/^pages\/[\w%-]+$/.test(path) && !url.search) return url.href;
    else if (path === 'search' && keys.every(key => ['q','type','page','options[prefix]'].includes(key))) return url.href;
  }

  function rule(action, url) {
    const script = document.createElement('script');
    script.type = 'speculationrules';
    if (loader.nonce) script.nonce = loader.nonce;
    script.textContent = JSON.stringify({[action]: [{urls: [url], eagerness: 'immediate'}]});
    document.head.append(script);
    return script;
  }

  function prioritize(url, handled) {
    document.dispatchEvent(new CustomEvent('ts:navigation-priority', {detail: {url, handled}}));
  }

  function prepare(link, strong) {
    if (!allowed() || !link) return;
    if (link.matches('ts-collection-results a[data-page-step], ts-collection-results a[data-page-number]')) {
      // The collection component handles this click by fetching a section.
      if (strong && !link.hidden) link.dispatchEvent(new CustomEvent('ts:prepare-page', {bubbles: true}));
      return;
    }
    const url = destination(link);
    if (!url) return;
    if (native && strong) {
      if (rendered?.url === url) return;
      rendered?.script.remove();
      rendered = {url, script: rule('prerender', url)};
    } else if (native) {
      if (prefetched.has(url) || rendered?.url === url) return;
      if (prefetched.size >= 4) {
        const oldest = prefetched.keys().next().value;
        prefetched.get(oldest).remove(); prefetched.delete(oldest);
      }
      prefetched.set(url, rule('prefetch', url));
    }
    prioritize(url, native);
  }

  function clearHover() {
    clearTimeout(hoverTimer);
    hoverLink = undefined;
  }

  function primaryLinks() {
    if (!allowed() || engaged) return;
    // These are the store's featured products, first collection listings, and
    // department cards. Only a small initial selection receives an HTML hint.
    const candidates = document.querySelectorAll('#popular-products a.product-image, #product-grid .product-title a, .ts-department__grid a.ts-department__card');
    const urls = new Set();
    for (const link of candidates) {
      const url = destination(link);
      if (!url || urls.has(url)) continue;
      urls.add(url); prepare(link, false);
      if (urls.size === (native ? 2 : 1)) break;
    }
  }

  function reset() {
    clearHover();
    rendered?.script.remove(); rendered = undefined;
    prefetched.forEach(script => script.remove()); prefetched.clear();
  }

  function start() {
    if (window.Shopify?.designMode) return;
    document.addEventListener('pointerover', event => {
      if (event.pointerType !== 'mouse' || !allowed()) return;
      const link = event.target.closest?.('a[href]');
      if (!link || hoverLink === link) return;
      clearHover(); hoverLink = link;
      hoverTimer = setTimeout(() => { engaged = true; prepare(link, true); }, 85);
    }, {passive: true});
    document.addEventListener('pointerout', event => {
      if (hoverLink && !hoverLink.contains(event.relatedTarget)) clearHover();
    }, {passive: true});
    document.addEventListener('focusin', event => {
      const link = event.target.closest?.('a[href]');
      if (link) { engaged = true; prepare(link, true); }
    });
    document.addEventListener('pointerdown', event => {
      if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[href]');
      if (link) { engaged = true; prepare(link, true); }
    }, {passive: true});
    document.addEventListener('ts:search-target', event => {
      engaged = true; prepare(event.detail.link, event.detail.selected);
    });
    function seed() { setTimeout(primaryLinks, 250); }
    if (document.readyState === 'complete') seed();
    else window.addEventListener('load', seed, {once: true});
    document.addEventListener('visibilitychange', () => { if (!allowed()) clearHover(); });
    window.addEventListener('pagehide', clearHover);
    window.addEventListener('pageshow', event => {
      if (event.persisted) { reset(); engaged = false; seed(); }
    });
    window.addEventListener('offline', reset);
    navigator.connection?.addEventListener?.('change', () => { if (!allowed()) reset(); });
  }
  if (document.prerendering) document.addEventListener('prerenderingchange', start, {once: true});
  else start();
})();
